import { supabase } from '../lib/supabase';

export interface FaqCategory {
  id: number;
  name: string;
  description?: string;
  icon: string;
  color: string;
  display_order: number;
  is_active: boolean;
  organization_id: string;
  created_at: string;
  updated_at: string;
  created_by?: string;
  updated_by?: string;
}

export interface FaqItem {
  id: number;
  category_id: number;
  question: string;
  answer: string;
  display_order: number;
  is_active: boolean;
  views_count: number;
  helpful_count: number;
  tags?: string[];
  organization_id: string;
  created_at: string;
  updated_at: string;
  created_by?: string;
  updated_by?: string;
  category?: FaqCategory;
}

export interface FaqFeedback {
  id: number;
  faq_item_id: number;
  user_id: string;
  is_helpful: boolean;
  feedback_text?: string;
  organization_id: string;
  created_at: string;
}

export interface CreateCategoryData {
  name: string;
  description?: string;
  icon?: string;
  color?: string;
  display_order?: number;
  is_active?: boolean;
}

export interface CreateFaqItemData {
  category_id: number;
  question: string;
  answer: string;
  display_order?: number;
  is_active?: boolean;
  tags?: string[];
}

export interface UpdateCategoryData extends Partial<CreateCategoryData> {
  id: number;
}

export interface UpdateFaqItemData extends Partial<CreateFaqItemData> {
  id: number;
}

class FaqService {
  // CATEGORIES
  async getCategories(organizationId: string): Promise<FaqCategory[]> {
    try {
      const { data, error } = await supabase
        .from('faq_categories')
        .select('*')
        .eq('organization_id', organizationId)
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching FAQ categories:', error);
      throw error;
    }
  }

  async createCategory(categoryData: CreateCategoryData, organizationId: string): Promise<FaqCategory> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { data, error } = await supabase
        .from('faq_categories')
        .insert({
          ...categoryData,
          organization_id: organizationId,
          created_by: user?.id,
          icon: categoryData.icon || 'HelpCircle',
          color: categoryData.color || '#6B7280',
          display_order: categoryData.display_order || 0,
          is_active: categoryData.is_active !== undefined ? categoryData.is_active : true
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating FAQ category:', error);
      throw error;
    }
  }

  async updateCategory(categoryData: UpdateCategoryData): Promise<FaqCategory> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { id, ...updateData } = categoryData;
      
      const { data, error } = await supabase
        .from('faq_categories')
        .update({
          ...updateData,
          updated_by: user?.id
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating FAQ category:', error);
      throw error;
    }
  }

  async deleteCategory(categoryId: number): Promise<void> {
    try {
      const { error } = await supabase
        .from('faq_categories')
        .delete()
        .eq('id', categoryId);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting FAQ category:', error);
      throw error;
    }
  }

  // FAQ ITEMS
  async getFaqItems(organizationId: string, categoryId?: number): Promise<FaqItem[]> {
    try {
      let query = supabase
        .from('faq_items')
        .select(`
          *,
          category:faq_categories(*)
        `)
        .eq('organization_id', organizationId)
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (categoryId) {
        query = query.eq('category_id', categoryId);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching FAQ items:', error);
      throw error;
    }
  }

  async createFaqItem(itemData: CreateFaqItemData, organizationId: string): Promise<FaqItem> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { data, error } = await supabase
        .from('faq_items')
        .insert({
          ...itemData,
          organization_id: organizationId,
          created_by: user?.id,
          display_order: itemData.display_order || 0,
          is_active: itemData.is_active !== undefined ? itemData.is_active : true,
          views_count: 0,
          helpful_count: 0
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating FAQ item:', error);
      throw error;
    }
  }

  async updateFaqItem(itemData: UpdateFaqItemData): Promise<FaqItem> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { id, ...updateData } = itemData;
      
      const { data, error } = await supabase
        .from('faq_items')
        .update({
          ...updateData,
          updated_by: user?.id
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating FAQ item:', error);
      throw error;
    }
  }

  async deleteFaqItem(itemId: number): Promise<void> {
    try {
      const { error } = await supabase
        .from('faq_items')
        .delete()
        .eq('id', itemId);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting FAQ item:', error);
      throw error;
    }
  }

  async incrementViews(itemId: number): Promise<void> {
    try {
      const { error } = await supabase
        .rpc('increment_faq_views', { faq_item_id: itemId });

      if (error) throw error;
    } catch (error) {
      console.error('Error incrementing FAQ views:', error);
      // Não fazer throw aqui para não quebrar a experiência do usuário
    }
  }

  // FEEDBACK
  async submitFeedback(itemId: number, isHelpful: boolean, feedbackText?: string, organizationId?: string): Promise<void> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user || !organizationId) return;

      // Verificar se o usuário já deu feedback para este item
      const { data: existingFeedback } = await supabase
        .from('faq_feedback')
        .select('id')
        .eq('faq_item_id', itemId)
        .eq('user_id', user.id)
        .single();

      if (existingFeedback) {
        // Atualizar feedback existente
        const { error } = await supabase
          .from('faq_feedback')
          .update({
            is_helpful: isHelpful,
            feedback_text: feedbackText
          })
          .eq('id', existingFeedback.id);

        if (error) throw error;
      } else {
        // Criar novo feedback
        const { error } = await supabase
          .from('faq_feedback')
          .insert({
            faq_item_id: itemId,
            user_id: user.id,
            is_helpful: isHelpful,
            feedback_text: feedbackText,
            organization_id: organizationId
          });

        if (error) throw error;
      }

      // Atualizar contador de helpful
      if (isHelpful) {
        await supabase
          .rpc('increment_faq_helpful', { faq_item_id: itemId });
      }
    } catch (error) {
      console.error('Error submitting FAQ feedback:', error);
      throw error;
    }
  }

  async getFeedback(organizationId: string, itemId?: number): Promise<FaqFeedback[]> {
    try {
      let query = supabase
        .from('faq_feedback')
        .select('*')
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false });

      if (itemId) {
        query = query.eq('faq_item_id', itemId);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching FAQ feedback:', error);
      throw error;
    }
  }

  // SEARCH
  async searchFaqs(organizationId: string, searchTerm: string): Promise<FaqItem[]> {
    try {
      const { data, error } = await supabase
        .from('faq_items')
        .select(`
          *,
          category:faq_categories(*)
        `)
        .eq('organization_id', organizationId)
        .eq('is_active', true)
        .or(`question.ilike.%${searchTerm}%,answer.ilike.%${searchTerm}%`)
        .order('views_count', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error searching FAQs:', error);
      throw error;
    }
  }

  // ANALYTICS
  async getFaqAnalytics(organizationId: string): Promise<{
    totalCategories: number;
    totalItems: number;
    totalViews: number;
    totalFeedback: number;
    helpfulPercentage: number;
    mostViewedItems: FaqItem[];
    leastHelpfulItems: FaqItem[];
  }> {
    try {
      const [categories, items, feedback] = await Promise.all([
        this.getCategories(organizationId),
        this.getFaqItems(organizationId),
        this.getFeedback(organizationId)
      ]);

      const totalViews = items.reduce((sum, item) => sum + item.views_count, 0);
      const helpfulFeedback = feedback.filter(f => f.is_helpful).length;
      const helpfulPercentage = feedback.length > 0 ? (helpfulFeedback / feedback.length) * 100 : 0;

      const mostViewedItems = [...items]
        .sort((a, b) => b.views_count - a.views_count)
        .slice(0, 5);

      const leastHelpfulItems = [...items]
        .filter(item => item.views_count > 0)
        .sort((a, b) => (a.helpful_count / Math.max(a.views_count, 1)) - (b.helpful_count / Math.max(b.views_count, 1)))
        .slice(0, 5);

      return {
        totalCategories: categories.length,
        totalItems: items.length,
        totalViews,
        totalFeedback: feedback.length,
        helpfulPercentage,
        mostViewedItems,
        leastHelpfulItems
      };
    } catch (error) {
      console.error('Error getting FAQ analytics:', error);
      throw error;
    }
  }
}

export const faqService = new FaqService(); 